/** @jsxImportSource @emotion/react */
import type { PropsWithChildren, ReactNode } from 'react';
import React, { useCallback } from 'react';
import type { Interpolation, Theme } from '@emotion/react';
import type { CSSProperties } from 'styled-components';
import { Link as ReactRouterLink } from 'react-router-dom';
import { P, match } from 'ts-pattern';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExternalLink } from '@fortawesome/free-solid-svg-icons';
import type { GlobalToken } from 'antd';
import Typography, { TypographyVariant } from '../Typography';
import type { ITheme } from '../../styles/theme';
import type { IPropsWithTestId } from '../../../constants/data-element-tracking-ids';
import {
  ELEMENT_ID_DATA_LOG_ATTRIBUTE,
  ELEMENT_ID_DATA_TEST_ATTRIBUTE,
} from '../../../constants/data-element-tracking-ids';
import { VideoPreviewImage } from '../VideoPlayer';
import { useEventLogger } from '../../../events/use-event-logger';
import { ClientEvent } from '../../../events/client-events';
import type { HtmlFromDotcms } from '../../../dotcms/dotcms-models/converters-from-dotcms';
import HtmlBox from '../HtmlBox';
import { AnchorButton, ButtonVariant, LinkButton } from '../Button';
import { BoxWithId } from '../BoxWithId/BoxWithId';

export enum MediaType {
  Image,
  Video,
}

const ROOT_STYLES: Interpolation<Theme> = {
  boxShadow: '0px 2px 2px 0px #00000026',
  height: '441px',
  width: '100%',
  minWidth: '220px',
  boxSizing: 'border-box',
  borderRadius: '10px',
  position: 'relative',
};

interface IProps extends IPropsWithTestId {
  label: string;
  themeToken: GlobalToken;
  theme: ITheme;
  mediaProps: {
    url: string;
    html?: HtmlFromDotcms;
    type?: MediaType;
    iconComponent?: ReactNode;
  };
  titleElement?: ReactNode;
  link?: {
    linkText: string;
    linkTo: string;
  } & IPropsWithTestId;
  additionalStyles?: Interpolation<Theme>;
  uniqueId?: string;
}

type ICardLinkOrAnchorWrapper = IPropsWithTestId &
Pick<IProps, 'link' | 'additionalStyles' | 'uniqueId'>;

const checkIsExternalLink = (link: string | undefined) =>
  !!link?.startsWith('http');

const CardLinkOrAnchorWrapper: React.FC<
PropsWithChildren<ICardLinkOrAnchorWrapper>
> = ({
  children, link, additionalStyles, uniqueId, dataElementId,
}) => {
  const { logEvent } = useEventLogger();
  const trackClick = useCallback(() => {
    if (uniqueId) {
      logEvent(ClientEvent.CLICKED_RESOURCE_LIBRARY_LINK, {
        unique_id: uniqueId,
      });
    }
  }, [logEvent, uniqueId]);

  const isExternalLink = checkIsExternalLink(link?.linkTo);
  // The card must be wrapped in a plain <a> if this is an external link,
  // or a react-router link if it's an internal link,
  // or a box if no link was specified.
  if (link && isExternalLink) {
    return (
      <a
        css={[
          // outline: revert is required to make tabindex focus still visible:
          { all: 'unset', outline: 'revert' },
          ROOT_STYLES,
          { cursor: link?.linkTo ? 'pointer' : undefined },
          additionalStyles,
        ]}
        href={link?.linkTo}
        onClickCapture={trackClick}
        target="_blank"
        rel="noreferrer"
        {...{
          [ELEMENT_ID_DATA_LOG_ATTRIBUTE]: link.dataElementId,
          [ELEMENT_ID_DATA_TEST_ATTRIBUTE]: link.dataElementId,
        }}
      >
        {children}
      </a>
    );
  }
  if (link) {
    return (
      <ReactRouterLink
        css={[ROOT_STYLES, { textDecoration: 'none' }, additionalStyles]}
        to={link?.linkTo}
        onClickCapture={trackClick}
        {...{
          [ELEMENT_ID_DATA_LOG_ATTRIBUTE]: link.dataElementId,
          [ELEMENT_ID_DATA_TEST_ATTRIBUTE]: link.dataElementId,
        }}
      >
        {children}
      </ReactRouterLink>
    );
  }
  return (
    <BoxWithId
      component="div"
      css={[ROOT_STYLES, additionalStyles]}
      onClickCapture={trackClick}
      dataElementId={dataElementId}
    >
      {children}
    </BoxWithId>
  );
};

const Card: React.FC<PropsWithChildren<IProps>> = ({
  label,
  theme,
  themeToken,
  mediaProps,
  titleElement,
  link,
  dataElementId,
  additionalStyles,
  children,
  uniqueId,
}) => {
  const isExternalLink = checkIsExternalLink(link?.linkTo);

  const mediaStyles: CSSProperties = {
    width: '100%',
    height: '170px',
    objectFit: 'cover',
    borderRadius: '10px 10px 0 0',
  };

  const mediaStylesInterpolation: Interpolation<Theme> = {
    width: '100%',
    height: '170px',
    objectFit: 'cover',
    borderRadius: '10px 10px 0 0',
  };

  const mediaIconStyles: Interpolation<Theme> = {
    position: 'absolute',
    top: '105px',
    left: '21px',
  };

  const infoStyles: Interpolation<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    padding: '32px 24px 27px 24px',
  };

  const titleStyles: Interpolation<Theme> = {
    padding: '6px 0 8px 0',
    display: '-webkit-box',
    WebkitLineClamp: '2',
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  };

  const descriptionStyles: Interpolation<Theme> = {
    display: '-webkit-box',
    WebkitLineClamp: '5',
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  };

  const linkStyles: Interpolation<Theme> = {
    position: 'absolute',
    bottom: '27px',
    top: 'auto',
  };

  const cardInnerLinkContents = match({ link, isExternalLink })
    .with({ link: undefined }, () => null)
    .with({ isExternalLink: true }, () =>
      (link ? (
        <AnchorButton
          href={link.linkTo}
          rel="noreferrer"
          icon={<FontAwesomeIcon icon={faExternalLink} />}
          iconPosition="end"
          themeToken={themeToken}
          dataElementId={link.dataElementId}
          additionalStyles={linkStyles}
          variant={ButtonVariant.TERTIARY}
          inlineButton
        >
          {link?.linkText}
        </AnchorButton>
      ) : null))
    .with({ isExternalLink: false }, () =>
      (link ? (
        <LinkButton
          to={link.linkTo}
          themeToken={themeToken}
          dataElementId={link.dataElementId}
          additionalStyles={linkStyles}
          variant={ButtonVariant.TERTIARY}
          inlineButton
        >
          {link.linkText}
        </LinkButton>
      ) : null))
    .exhaustive();

  const mediaTestId = `${dataElementId}-card-media`;
  const media = match({
    mediaType: mediaProps.type,
    mediaHtml: mediaProps.html,
  })
    .with({ mediaType: MediaType.Video }, () => (
      <VideoPreviewImage
        theme={theme}
        videoUrl={mediaProps.url}
        additionalStyles={mediaStyles}
        width={mediaStyles.width}
        height={mediaStyles.height}
        dataElementId={mediaTestId}
      />
    ))
    .with({ mediaType: MediaType.Image, mediaHtml: P.not(undefined) }, () => (
      <HtmlBox
        html={mediaProps.html}
        additionalStyles={{ svg: mediaStylesInterpolation }}
        dataElementId={mediaTestId}
      />
    ))
    .otherwise(() => (
      <img
        src={mediaProps.url}
        alt=""
        style={mediaStyles}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...{
          [ELEMENT_ID_DATA_LOG_ATTRIBUTE]: mediaTestId,
          [ELEMENT_ID_DATA_TEST_ATTRIBUTE]: mediaTestId,
        }}
      />
    ));

  return (
    <CardLinkOrAnchorWrapper
      link={link}
      additionalStyles={additionalStyles}
      uniqueId={uniqueId}
      dataElementId={dataElementId}
    >
      {media}
      <BoxWithId css={mediaIconStyles} dataElementId={`${dataElementId}-icon`}>
        {mediaProps.iconComponent}
      </BoxWithId>
      <div css={infoStyles}>
        <Typography
          variant={TypographyVariant.SMALL_EYEBROW}
          theme={theme}
          dataElementId={`${dataElementId}-label`}
        >
          {label}
        </Typography>
        {!!titleElement && (
          <Typography
            variant={TypographyVariant.LARGE_BODY_BOLD}
            theme={theme}
            additionalStyles={titleStyles}
            dataElementId={`${dataElementId}-title`}
          >
            {titleElement}
          </Typography>
        )}
        {!!children && (
          <Typography
            variant={TypographyVariant.SMALL_BODY_REGULAR}
            theme={theme}
            additionalStyles={descriptionStyles}
            dataElementId={`${dataElementId}-description`}
          >
            {children}
          </Typography>
        )}
        {cardInnerLinkContents}
      </div>
    </CardLinkOrAnchorWrapper>
  );
};

export default Card;
